import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLayoutSlots } from './LayoutSlotsContext';
import type { BreadcrumbItem } from './LayoutSlotsContext';

interface LayoutSubSidebarProps {
  title?: string;
  padded?: boolean;
  children?: ReactNode;
  defaultCollapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

export function LayoutSubSidebar({
  title,
  padded = true,
  children,
  defaultCollapsed,
  onCollapsedChange,
}: LayoutSubSidebarProps) {
  const { sidebarPortalEl, setSidebarSlot, clearSidebarSlot } =
    useLayoutSlots();

  // Ref so the stable toggle wrapper always calls the latest callback without
  // causing the setup effect to re-run on every render.
  const onCollapsedChangeRef = useRef(onCollapsedChange);
  useEffect(() => {
    onCollapsedChangeRef.current = onCollapsedChange;
  }, [onCollapsedChange]);

  const stableOnToggle = useCallback((collapsed: boolean) => {
    onCollapsedChangeRef.current?.(collapsed);
  }, []);

  useEffect(() => {
    setSidebarSlot(title, padded, defaultCollapsed ?? false, stableOnToggle);
    return () => {
      clearSidebarSlot();
    };
  }, [
    title,
    padded,
    defaultCollapsed,
    stableOnToggle,
    setSidebarSlot,
    clearSidebarSlot,
  ]);

  if (!sidebarPortalEl) return null;
  return createPortal(children, sidebarPortalEl);
}

interface LayoutSubHeaderProps {
  title?: string;
  children?: ReactNode;
}

export function LayoutSubHeader({ title, children }: LayoutSubHeaderProps) {
  const { headerPortalEl, setHeaderSlot, clearHeaderSlot } = useLayoutSlots();

  useEffect(() => {
    setHeaderSlot(title);
    return () => clearHeaderSlot();
  }, [title, setHeaderSlot, clearHeaderSlot]);

  if (!headerPortalEl) return null;
  return createPortal(children, headerPortalEl);
}

/**
 * Sets the page title displayed in the Header <h1>.
 * Each page should render this to declare its own title.
 */
interface LayoutPageTitleProps {
  title: string;
}

export function LayoutPageTitle({ title }: LayoutPageTitleProps) {
  const { setPageTitle, clearPageTitle } = useLayoutSlots();

  useEffect(() => {
    setPageTitle(title);
    return () => clearPageTitle();
  }, [title, setPageTitle, clearPageTitle]);

  return null;
}

/**
 * Sets multi-level breadcrumbs in the Header.
 * Items with a `to` property render as links; the last item is the current page.
 */
interface LayoutBreadcrumbsProps {
  items: BreadcrumbItem[];
}

export function LayoutBreadcrumbs({ items }: LayoutBreadcrumbsProps) {
  const { setBreadcrumbs, clearBreadcrumbs } = useLayoutSlots();

  useEffect(() => {
    setBreadcrumbs(items);
    return () => clearBreadcrumbs();
  }, [items, setBreadcrumbs, clearBreadcrumbs]);

  return null;
}
